import { Upload, message } from 'antd';
import type { UploadProps } from 'antd/es/upload/interface';
import { RcFile } from 'antd/es/upload';
import { GalleryExport } from 'iconsax-react';
import { useSearchParams } from 'react-router-dom';

import { UploadedImage } from '../../../../types/chat';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../../../store/slices/chat';
import { useUploadChatDocumentsMutation } from '../../../../store/services/chatService';

interface ImageUploadInputProps {
  onImagesChange: (
    images: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[])
  ) => void;
  onImageIdsChange: (id: string) => void;
  isUploading: boolean;
  onUploadingChange: (isUploading: boolean) => void;
  onRemove: (uid: string, imageIds: string[]) => void;
  setInputLoading: Dispatch<SetStateAction<boolean>>;
  roomId?: string;
}

const ImageUploadInput = ({
  onImagesChange,
  onImageIdsChange,
  onUploadingChange,
  setInputLoading,
  roomId: propRoomId,
}: ImageUploadInputProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { selectedRoomId } = useChat();
  const effectiveRoomId =
    propRoomId || selectedRoomId || searchParams.get('chatRoomId') || undefined;

  const [uploadChatDocuments] = useUploadChatDocumentsMutation();

  const beforeUploadImage = (file: RcFile) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error(t('invalid_file_type'));
    }
    return isImage || Upload.LIST_IGNORE;
  };

  const uploadFile = async (file: RcFile) => {
    setInputLoading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);

      const res = await uploadChatDocuments({
        formData,
        ...(effectiveRoomId && { roomId: effectiveRoomId }),
      }).unwrap();

      return res;
    } catch (error) {
      console.error('Upload failed:', error);
      message.error(t('somthingWentWrong'));
      return null;
    } finally {
      setInputLoading(false);
    }
  };

  const handleImageUpload: UploadProps['onChange'] = async (info) => {
    if (info.file.status === 'uploading') {
      onUploadingChange(true);
      onImagesChange((prev: UploadedImage[]) => {
        const existing = prev.find((img) => img.uid === info.file.uid);
        if (existing) {
          return prev.map((img) =>
            img.uid === info.file.uid
              ? { ...img, percent: info.file.percent }
              : img
          );
        }
        return [
          ...prev,
          {
            uid: info.file.uid,
            name: info.file.name,
            status: 'uploading',
            url: URL.createObjectURL(info.file.originFileObj as Blob),
            percent: 0,
          },
        ];
      });
    } else if (info.file.status === 'done') {
      const res = await uploadFile(info.file.originFileObj as RcFile);
      if (res?.payload?.[0]?.id) {
        onImageIdsChange(res?.payload[0]?.id);
        onImagesChange((prev: UploadedImage[]) =>
          prev.map((img) =>
            img.uid === info.file.uid
              ? { ...img, status: 'done', percent: 100 }
              : img
          )
        );
        message.success(`${info.file.name} uploaded successfully`);
      } else {
        onImagesChange((prev: UploadedImage[]) =>
          prev.filter((img) => img.uid !== info.file.uid)
        );
        message.error(`${info.file.name} upload failed.`);
      }
      onUploadingChange(false);
    } else if (info.file.status === 'error') {
      onImagesChange((prev: UploadedImage[]) =>
        prev.filter((img) => img.uid !== info.file.uid)
      );
      onUploadingChange(false);
      message.error(`${info.file.name} upload failed.`);
    }
  };

  return (
    <Upload
      accept="image/*"
      showUploadList={false}
      beforeUpload={beforeUploadImage}
      onChange={handleImageUpload}
      multiple
      customRequest={({ onSuccess }) => {
        onSuccess?.('ok');
      }}
      data-testid="chat-image-upload-input"
    >
      <div
        className="flex gap-2 items-start hover:bg-black-100 p-2 rounded-lg cursor-pointer"
        data-testid="chat-image-upload-trigger"
      >
        <GalleryExport size={20} />
        <span data-testid="chat-image-upload-label">{t('attachImage')} (PNG,JPG,JPEG)</span>
      </div>
    </Upload>
  );
};

export default ImageUploadInput;

